import React, { useState, useEffect, useMemo } from "react";
import { collection, getDocs, query, Timestamp } from "firebase/firestore";
import { db } from "./firebase";
import { useAdmin } from "./hooks/useAdmin";
import "./AdminCompetitionStats.css";

export default function AdminCompetitionStats() {
  const { isAdmin, loading: adminLoading } = useAdmin();
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [selectedYear, setSelectedYear] = useState("all");
  const [selectedDiscipline, setSelectedDiscipline] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedCompetition, setSelectedCompetition] = useState("all");
  
  // Tabs
  const [activeTab, setActiveTab] = useState("overview");

  // Fetch all competition data
  useEffect(() => {
    const loadCompetitionData = async () => {
      setLoading(true);
      setError(null);

      try {
        const competitionData = [];

        // Query old structure
        const oldSnapshot = await getDocs(query(collection(db, "competitions")));
        
        oldSnapshot.forEach((doc) => {
          const comp = doc.data();
          const compId = doc.id;
          const compName = comp.name || compId;
          
          if (comp.timestamp && comp.results) {
            const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
            const year = compDate.getFullYear();
            
            // Extract unique dancers
            const uniqueDancers = new Set();
            comp.results.forEach(r => {
              if (r.dancerName) uniqueDancers.add(r.dancerName.toLowerCase().trim());
            });

            competitionData.push({
              id: compId,
              name: compName,
              date: compDate,
              year: year,
              discipline: comp.discipline || "Latin",
              category: comp.category || "-",
              dance: comp.dance || null,
              participantCount: comp.results.length,
              uniqueParticipants: uniqueDancers.size,
              dancers: Array.from(uniqueDancers),
              results: comp.results,
              structure: "old"
            });
          }
        });

        // Query new structure
        const disciplinesSnapshot = await getDocs(collection(db, "disciplines"));
        
        for (const disciplineDoc of disciplinesSnapshot.docs) {
          const disciplineName = disciplineDoc.id;
          const categoriesSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories"));
          
          for (const categoryDoc of categoriesSnapshot.docs) {
            const categoryName = categoryDoc.id;
            
            // Direct competitions
            try {
              const compsSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "competitions"));
              
              compsSnapshot.forEach((doc) => {
                const comp = doc.data();
                if (comp.timestamp && comp.results) {
                  const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
                  const year = compDate.getFullYear();
                  
                  const readableCategory = categoryName.split("_").map(word => 
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(" ");
                  
                  const uniqueDancers = new Set();
                  comp.results.forEach(r => {
                    if (r.dancerName) uniqueDancers.add(r.dancerName.toLowerCase().trim());
                  });

                  competitionData.push({
                    id: doc.id,
                    name: comp.name || doc.id,
                    date: compDate,
                    year: year,
                    discipline: disciplineName,
                    category: readableCategory,
                    dance: null,
                    participantCount: comp.results.length,
                    uniqueParticipants: uniqueDancers.size,
                    dancers: Array.from(uniqueDancers),
                    results: comp.results,
                    structure: "new-direct"
                  });
                }
              });
            } catch (e) { /* no direct competitions */ }
            
            // Dance-specific competitions
            try {
              const dancesSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "dances"));
              
              for (const danceDoc of dancesSnapshot.docs) {
                const danceName = danceDoc.id;
                const danceCompsSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "dances", danceName, "competitions"));
                
                danceCompsSnapshot.forEach((doc) => {
                  const comp = doc.data();
                  if (comp.timestamp && comp.results) {
                    const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp);
                    const year = compDate.getFullYear();
                    
                    const readableCategory = categoryName.split("_").map(word => 
                      word.charAt(0).toUpperCase() + word.slice(1)
                    ).join(" ");
                    
                    const readableDance = danceName.charAt(0).toUpperCase() + danceName.slice(1);
                    
                    const uniqueDancers = new Set();
                    comp.results.forEach(r => {
                      if (r.dancerName) uniqueDancers.add(r.dancerName.toLowerCase().trim());
                    });

                    competitionData.push({
                      id: doc.id,
                      name: comp.name || doc.id,
                      date: compDate,
                      year: year,
                      discipline: disciplineName,
                      category: readableCategory,
                      dance: readableDance,
                      participantCount: comp.results.length,
                      uniqueParticipants: uniqueDancers.size,
                      dancers: Array.from(uniqueDancers),
                      results: comp.results,
                      structure: "new-dance"
                    });
                  }
                });
              }
            } catch (e) { /* no dances */ }
          }
        }

        competitionData.sort((a, b) => b.date - a.date);
        setCompetitions(competitionData);
      } catch (err) {
        console.error("Error loading competition data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadCompetitionData();
  }, []);

  // Helper function to extract base competition name (remove year suffix)
  const getBaseCompetitionName = (name) => {
    // Remove year patterns like "2023", "2024", "2025" from end or anywhere
    // Also handle patterns like "'23", "'24", etc.
    return name
      .replace(/\s*20\d{2}\s*/g, ' ')  // Remove 2020-2029
      .replace(/\s*'\d{2}\s*/g, ' ')    // Remove '23, '24, etc.
      .replace(/\s+/g, ' ')             // Normalize spaces
      .trim();
  };

  // Get available filter options
  const filterOptions = useMemo(() => {
    const years = [...new Set(competitions.map(c => c.year))].sort((a, b) => b - a);
    const disciplines = [...new Set(competitions.map(c => c.discipline))].sort();
    const categories = [...new Set(competitions.map(c => c.category))].sort();
    // Use base competition names (without year) for filtering
    const competitionNames = [...new Set(competitions.map(c => getBaseCompetitionName(c.name)))].sort();
    
    return { years, disciplines, categories, competitionNames };
  }, [competitions]);

  // Filtered competitions
  const filteredCompetitions = useMemo(() => {
    return competitions.filter(c => {
      if (selectedYear !== "all" && c.year !== parseInt(selectedYear)) return false;
      if (selectedDiscipline !== "all" && c.discipline !== selectedDiscipline) return false;
      if (selectedCategory !== "all" && c.category !== selectedCategory) return false;
      if (selectedCompetition !== "all" && getBaseCompetitionName(c.name) !== selectedCompetition) return false;
      return true;
    });
  }, [competitions, selectedYear, selectedDiscipline, selectedCategory, selectedCompetition]);

  // Calculate statistics
  const stats = useMemo(() => {
    // Total unique participants across all filtered competitions
    const allDancers = new Set();
    filteredCompetitions.forEach(c => {
      c.dancers.forEach(d => allDancers.add(d));
    });

    // Group by BASE competition name for growth analysis (e.g., "Greek Open" groups all years)
    const byBaseName = {};
    filteredCompetitions.forEach(c => {
      const baseName = getBaseCompetitionName(c.name);
      if (!byBaseName[baseName]) {
        byBaseName[baseName] = [];
      }
      byBaseName[baseName].push(c);
    });

    // Sort each competition's entries by year
    Object.keys(byBaseName).forEach(name => {
      byBaseName[name].sort((a, b) => a.year - b.year);
    });

    // Competition series (grouped by base name, showing year-over-year data)
    const competitionSeries = Object.entries(byBaseName)
      .filter(([_, entries]) => {
        const years = new Set(entries.map(e => e.year));
        return years.size > 1; // Only show series with multiple years
      })
      .map(([name, entries]) => {
        const years = [...new Set(entries.map(e => e.year))].sort();
        const participantsByYear = {};
        const uniqueByYear = {};
        
        years.forEach(year => {
          const yearEntries = entries.filter(e => e.year === year);
          participantsByYear[year] = yearEntries.reduce((sum, e) => sum + e.participantCount, 0);
          
          const uniqueDancers = new Set();
          yearEntries.forEach(e => e.dancers.forEach(d => uniqueDancers.add(d)));
          uniqueByYear[year] = uniqueDancers.size;
        });

        // Calculate growth
        const growthData = [];
        for (let i = 1; i < years.length; i++) {
          const prevYear = years[i - 1];
          const currYear = years[i];
          const growth = uniqueByYear[prevYear] > 0 
            ? ((uniqueByYear[currYear] - uniqueByYear[prevYear]) / uniqueByYear[prevYear] * 100).toFixed(1)
            : 0;
          growthData.push({
            from: prevYear,
            to: currYear,
            growth: parseFloat(growth),
            prevCount: uniqueByYear[prevYear],
            currCount: uniqueByYear[currYear]
          });
        }

        return {
          name,
          years,
          participantsByYear,
          uniqueByYear,
          growthData,
          totalEntries: entries.length,
          latestYear: Math.max(...years),
          latestCount: uniqueByYear[Math.max(...years)]
        };
      })
      .sort((a, b) => b.latestCount - a.latestCount);

    // By discipline stats
    const byDiscipline = {};
    filteredCompetitions.forEach(c => {
      if (!byDiscipline[c.discipline]) {
        byDiscipline[c.discipline] = { count: 0, participants: 0, dancers: new Set() };
      }
      byDiscipline[c.discipline].count++;
      byDiscipline[c.discipline].participants += c.participantCount;
      c.dancers.forEach(d => byDiscipline[c.discipline].dancers.add(d));
    });

    // By category stats
    const byCategory = {};
    filteredCompetitions.forEach(c => {
      if (!byCategory[c.category]) {
        byCategory[c.category] = { count: 0, participants: 0, dancers: new Set() };
      }
      byCategory[c.category].count++;
      byCategory[c.category].participants += c.participantCount;
      c.dancers.forEach(d => byCategory[c.category].dancers.add(d));
    });

    // By year stats
    const byYear = {};
    filteredCompetitions.forEach(c => {
      if (!byYear[c.year]) {
        byYear[c.year] = { count: 0, participants: 0, dancers: new Set() };
      }
      byYear[c.year].count++;
      byYear[c.year].participants += c.participantCount;
      c.dancers.forEach(d => byYear[c.year].dancers.add(d));
    });

    // All competition series (including single-year ones)
    const allSeries = Object.entries(byBaseName)
      .map(([name, entries]) => {
        const years = [...new Set(entries.map(e => e.year))].sort();
        const uniqueDancers = new Set();
        entries.forEach(e => e.dancers.forEach(d => uniqueDancers.add(d)));
        
        return {
          name,
          years,
          totalEntries: entries.length,
          uniqueDancers: uniqueDancers.size,
          latestYear: Math.max(...years)
        };
      })
      .sort((a, b) => b.uniqueDancers - a.uniqueDancers);

    return {
      totalCompetitions: filteredCompetitions.length,
      totalParticipants: filteredCompetitions.reduce((sum, c) => sum + c.participantCount, 0),
      uniqueDancers: allDancers.size,
      avgParticipants: filteredCompetitions.length > 0 
        ? (filteredCompetitions.reduce((sum, c) => sum + c.uniqueParticipants, 0) / filteredCompetitions.length).toFixed(1)
        : 0,
      competitionSeries,
      allSeries,
      byDiscipline,
      byCategory,
      byYear,
      byBaseName
    };
  }, [filteredCompetitions]);

  if (adminLoading || loading) {
    return (
      <div className="admin-comp-stats">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading competition statistics...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="admin-comp-stats">
        <div className="access-denied">
          <h2>🔒 Access Denied</h2>
          <p>You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-comp-stats">
        <div className="error-container">
          <h2>❌ Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-comp-stats">
      <div className="stats-header">
        <h1>📊 Competition Statistics</h1>
        <p className="stats-subtitle">Analyze competition data, participant trends, and year-over-year growth</p>
      </div>

      {/* Filters */}
      <div className="stats-filters">
        <div className="filter-group">
          <label>Year</label>
          <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
            <option value="all">All Years</option>
            {filterOptions.years.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Discipline</label>
          <select value={selectedDiscipline} onChange={(e) => setSelectedDiscipline(e.target.value)}>
            <option value="all">All Disciplines</option>
            {filterOptions.disciplines.map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Category</label>
          <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
            <option value="all">All Categories</option>
            {filterOptions.categories.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Competition Series</label>
          <select value={selectedCompetition} onChange={(e) => setSelectedCompetition(e.target.value)}>
            <option value="all">All Competition Series</option>
            {filterOptions.competitionNames.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <button 
          className="reset-filters-btn"
          onClick={() => {
            setSelectedYear("all");
            setSelectedDiscipline("all");
            setSelectedCategory("all");
            setSelectedCompetition("all");
          }}
        >
          🔄 Reset Filters
        </button>
      </div>

      {/* Tabs */}
      <div className="stats-tabs">
        <button 
          className={`tab-btn ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          📈 Overview
        </button>
        <button 
          className={`tab-btn ${activeTab === "growth" ? "active" : ""}`}
          onClick={() => setActiveTab("growth")}
        >
          📊 Growth Analysis
        </button>
        <button 
          className={`tab-btn ${activeTab === "breakdown" ? "active" : ""}`}
          onClick={() => setActiveTab("breakdown")}
        >
          📋 Breakdown
        </button>
        <button 
          className={`tab-btn ${activeTab === "competitions" ? "active" : ""}`}
          onClick={() => setActiveTab("competitions")}
        >
          🏆 All Competitions
        </button>
      </div>

      {/* Tab Content */}
      <div className="stats-content">
        {activeTab === "overview" && (
          <div className="overview-tab">
            {/* Summary Cards */}
            <div className="summary-cards">
              <div className="summary-card">
                <div className="card-icon">🏆</div>
                <div className="card-content">
                  <span className="card-value">{stats.totalCompetitions}</span>
                  <span className="card-label">Total Competitions</span>
                </div>
              </div>
              <div className="summary-card">
                <div className="card-icon">👥</div>
                <div className="card-content">
                  <span className="card-value">{stats.totalParticipants.toLocaleString()}</span>
                  <span className="card-label">Total Entries</span>
                </div>
              </div>
              <div className="summary-card highlight">
                <div className="card-icon">🎭</div>
                <div className="card-content">
                  <span className="card-value">{stats.uniqueDancers.toLocaleString()}</span>
                  <span className="card-label">Unique Dancers</span>
                </div>
              </div>
              <div className="summary-card">
                <div className="card-icon">📊</div>
                <div className="card-content">
                  <span className="card-value">{stats.avgParticipants}</span>
                  <span className="card-label">Avg. per Competition</span>
                </div>
              </div>
            </div>

            {/* Year-over-Year Chart */}
            <div className="chart-section">
              <h3>📅 Participants by Year</h3>
              <div className="year-chart">
                {Object.entries(stats.byYear)
                  .sort(([a], [b]) => parseInt(a) - parseInt(b))
                  .map(([year, data]) => {
                    const maxDancers = Math.max(...Object.values(stats.byYear).map(d => d.dancers.size));
                    const percentage = (data.dancers.size / maxDancers) * 100;
                    return (
                      <div key={year} className="year-bar-container">
                        <span className="year-label">{year}</span>
                        <div className="year-bar-wrapper">
                          <div 
                            className="year-bar" 
                            style={{ width: `${percentage}%` }}
                          >
                            <span className="year-value">{data.dancers.size} dancers</span>
                          </div>
                        </div>
                        <span className="year-comps">{data.count} comps</span>
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* Top Competition Series */}
            <div className="top-section">
              <h3>🏅 Largest Competition Series (by total unique participants)</h3>
              <div className="top-list">
                {stats.allSeries
                  .slice(0, 10)
                  .map((series, idx) => (
                    <div key={series.name} className="top-item">
                      <span className="top-rank">#{idx + 1}</span>
                      <div className="top-info">
                        <span className="top-name">{series.name}</span>
                        <span className="top-meta">
                          {series.years.length} year{series.years.length > 1 ? 's' : ''}: {series.years.join(', ')} • {series.totalEntries} competition entries
                        </span>
                      </div>
                      <span className="top-count">{series.uniqueDancers} dancers</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === "growth" && (
          <div className="growth-tab">
            <h3>📈 Competition Series - Year over Year Growth</h3>
            <p className="growth-description">
              Competition series that have been held across multiple years (e.g., Greek Open, Salonika Open), showing participant growth trends.
            </p>
            
            {stats.competitionSeries.length === 0 ? (
              <div className="no-data">
                <p>No competition series found with multiple years in the current filters.</p>
              </div>
            ) : (
              <div className="growth-grid">
                {stats.competitionSeries.map(comp => (
                  <div key={comp.name} className="growth-card">
                    <div className="growth-header">
                      <h4>{comp.name}</h4>
                      <span className="years-badge">{comp.years.length} years</span>
                    </div>
                    
                    <div className="growth-timeline">
                      {comp.years.map((year, idx) => (
                        <div key={year} className="timeline-item">
                          <div className="timeline-year">{year}</div>
                          <div className="timeline-count">{comp.uniqueByYear[year]} dancers</div>
                          {idx < comp.years.length - 1 && comp.growthData[idx] && (
                            <div className={`timeline-growth ${comp.growthData[idx].growth >= 0 ? "positive" : "negative"}`}>
                              {comp.growthData[idx].growth >= 0 ? "↑" : "↓"} {Math.abs(comp.growthData[idx].growth)}%
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="growth-summary">
                      {comp.growthData.length > 0 && (
                        <div className="overall-growth">
                          <span className="label">Latest Growth:</span>
                          <span className={`value ${comp.growthData[comp.growthData.length - 1].growth >= 0 ? "positive" : "negative"}`}>
                            {comp.growthData[comp.growthData.length - 1].growth >= 0 ? "+" : ""}
                            {comp.growthData[comp.growthData.length - 1].growth}%
                          </span>
                        </div>
                      )}
                      <div className="total-growth">
                        <span className="label">Total ({comp.years[0]} → {comp.years[comp.years.length - 1]}):</span>
                        <span className={`value ${comp.uniqueByYear[comp.years[comp.years.length - 1]] >= comp.uniqueByYear[comp.years[0]] ? "positive" : "negative"}`}>
                          {comp.uniqueByYear[comp.years[0]] > 0 
                            ? `${(((comp.uniqueByYear[comp.years[comp.years.length - 1]] - comp.uniqueByYear[comp.years[0]]) / comp.uniqueByYear[comp.years[0]]) * 100).toFixed(1)}%`
                            : "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "breakdown" && (
          <div className="breakdown-tab">
            <div className="breakdown-grid">
              {/* By Discipline */}
              <div className="breakdown-section">
                <h3>💃 By Discipline</h3>
                <div className="breakdown-list">
                  {Object.entries(stats.byDiscipline)
                    .sort(([, a], [, b]) => b.dancers.size - a.dancers.size)
                    .map(([discipline, data]) => (
                      <div key={discipline} className="breakdown-item">
                        <div className="breakdown-name">{discipline}</div>
                        <div className="breakdown-stats">
                          <span className="stat-item">
                            <strong>{data.count}</strong> competitions
                          </span>
                          <span className="stat-item">
                            <strong>{data.dancers.size}</strong> unique dancers
                          </span>
                          <span className="stat-item">
                            <strong>{data.participants}</strong> total entries
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* By Category */}
              <div className="breakdown-section">
                <h3>🏷️ By Category</h3>
                <div className="breakdown-list">
                  {Object.entries(stats.byCategory)
                    .sort(([, a], [, b]) => b.dancers.size - a.dancers.size)
                    .map(([category, data]) => (
                      <div key={category} className="breakdown-item">
                        <div className="breakdown-name">{category}</div>
                        <div className="breakdown-stats">
                          <span className="stat-item">
                            <strong>{data.count}</strong> competitions
                          </span>
                          <span className="stat-item">
                            <strong>{data.dancers.size}</strong> unique dancers
                          </span>
                          <span className="stat-item">
                            <strong>{data.participants}</strong> total entries
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "competitions" && (
          <div className="competitions-tab">
            <h3>🏆 All Competitions ({filteredCompetitions.length})</h3>
            <div className="competitions-table-container">
              <table className="competitions-table">
                <thead>
                  <tr>
                    <th>Competition</th>
                    <th>Date</th>
                    <th>Discipline</th>
                    <th>Category</th>
                    <th>Dance</th>
                    <th>Unique Dancers</th>
                    <th>Total Entries</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCompetitions.map(comp => (
                    <tr key={comp.id}>
                      <td className="comp-name">{comp.name}</td>
                      <td>{comp.date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</td>
                      <td>{comp.discipline}</td>
                      <td>{comp.category}</td>
                      <td>{comp.dance || "-"}</td>
                      <td className="count-cell">{comp.uniqueParticipants}</td>
                      <td className="count-cell">{comp.participantCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
